import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ChoosePlanBuyng } from '../../models/choose-plan/choose-plan-buyng';
import { ClientData } from '../../models/client-data/client-data';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractSummary } from '../../models/contract-summary/contract-summary';
import { DownloadFile } from '../../models/contract/download-file';
import { FileType } from '../../models/enum/file-type';
import { PaymentContractSummaryShort } from '../../models/payment-method/payment-contract-summary-short';
import { PaymentMethodRequest } from '../../models/payment-method/payment-method-request';
import { ContractPaymentSignature } from '../../models/payment-signature/contract-payment-signature';
import { VinValidate } from "../../models/vin-validate/vin-validate";
import { ContractService } from '../../services/contract/contract.service';
import { ImageService } from '../../services/image/image.service';
import { PaymentMethodService } from '../../services/payment-method/payment-method.service';
import { SignaturePadComponent } from '../../shared/components/signature-pad/signature-pad.component';

@Component({
	selector: 'app-payment-signature',
	templateUrl: './payment-signature.component.html',
	styleUrls: ['./payment-signature.component.scss'],
	standalone: false
})
export class PaymentSignatureComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	isContractCreated = false;
	isSubmitted = false;
	blockedUi = false;
	isDownloadingContract = false;
	contractIdDownload = 0;

	paymentSignatureForm!: UntypedFormGroup;
	invalidSignature = true;
	signatureImgBase64!: string;
	vinValidate!: VinValidate;
	clientData!: ClientData;
	contractSummary!: ContractSummary;
	termsUser = false;
	dateAttachment = new Date();
	choosePlanBuyng: ChoosePlanBuyng;
	paymentContractSummaryShort: PaymentContractSummaryShort;
	contractPaymentSignature!: ContractPaymentSignature;
	paymentMethodRequest!: PaymentMethodRequest;
	contract!: number;
	document!: string;
	isAllowed!: boolean;
	productCode!: string;
	errorContract = false;
	uuid!: string;

	@ViewChild('signaturePad') signaturePadCmp?: SignaturePadComponent;
	@ViewChild('termsConsent', { read: ElementRef }) termsConsentRef?: ElementRef<HTMLElement>;
	submitted = false;
	isDrawing = false;
	termsFocusActive = false;

	constructor(
		private messageService: MessageService,
		private fb: UntypedFormBuilder,
		private router: Router,
		private translateService: TranslateService,
		private paymentMethodService: PaymentMethodService,
		private contractService: ContractService,
		private activatedRoute: ActivatedRoute,
		private imageService: ImageService
	) {
		this.choosePlanBuyng = new ChoosePlanBuyng;
		this.paymentContractSummaryShort = new PaymentContractSummaryShort;
	}

	ngOnInit() {
		this.activatedRoute.queryParams.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(params => {

			this.uuid = params.uuid;
			this.contractSummarySearch(this.uuid);
		});

		this.paymentSignatureForm = this.fb.group({
			termsUser: [false, Validators.requiredTrue],
			signature: [null, Validators.required],
		});
	}

	contractSummarySearch(uuid: string) {
		this.paymentMethodService
			.getPaymentContractSummaryShort(uuid)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: PaymentContractSummaryShort) => {

				this.paymentContractSummaryShort = response;

				this.contractPaymentSignature = this.setContractPaymentSignature();

				this.productCode = this.paymentContractSummaryShort.productCode;

				if (this.productCode === null) {
					this.productCode = this.paymentContractSummaryShort.maintenance.productCode;
				}
				this.handleRouteLogic();

			}, (error) => {

				this.errorContract = true;

				if (error.status === 404) {

					this.messageService.add(
						{
							severity: 'error', summary: 'Erro', detail: 'Contrato não encontrado.'
						}
					);
				}

				this.messageService.add(
					{
						severity: 'error', summary: 'Erro'
						, detail: 'Não foi possível buscar informações do contrato.'
					});
			});
	}

	handleRouteLogic() {
		if (this.paymentContractSummaryShort.extension === undefined && this.paymentContractSummaryShort.country !== 'BRA') {
			this.router.navigate(['payment-method']
				, {
					queryParams: {
						uuid: this.uuid
					}
				});
		}
	}

	get f(): { [key: string]: AbstractControl } {

		return this.paymentSignatureForm.controls;
	}

	drawComplete(): void {

		const signatureCtrl = this.paymentSignatureForm.get('signature');
		signatureCtrl?.markAsTouched();
		signatureCtrl?.updateValueAndValidity({ onlySelf: true });
	}

	drawClean(): void {

		this.signaturePadCmp?.clear();
		const signatureCtrl = this.paymentSignatureForm.get('signature');
		signatureCtrl?.setValue(null);
		signatureCtrl?.markAsTouched();
		signatureCtrl?.updateValueAndValidity({ onlySelf: true });
	}

	private focusTermsConsent(): boolean {
		const termsHost = this.termsConsentRef?.nativeElement;
		if (!termsHost) {
			return false;
		}

		const termsInput = termsHost.querySelector('.p-checkbox-input') as HTMLInputElement | null;
		const termsBox = termsHost.querySelector('.p-checkbox-box') as HTMLElement | null;
		if (!termsInput && !termsBox) {
			return false;
		}

		this.termsFocusActive = true;
		setTimeout(() => {
			this.termsFocusActive = false;
		}, 1300);

		termsHost.scrollIntoView({ behavior: 'smooth', block: 'center' });
		if (termsInput) {
			termsInput.focus();
		} else if (termsBox) {
			termsBox.setAttribute('tabindex', '-1');
			termsBox.focus();
		}
		return true;
	}

	getDay(): number {
		return this.dateAttachment.getDate();
	}

	getMonth(): string {

		const numberMonth = this.dateAttachment.getMonth();
		let nameMonth = '';

		switch (numberMonth) {

			case 0:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JANUARY.UPPER.CASE');
				break;

			case 1:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.FEBRUARY.UPPER.CASE');
				break;

			case 2:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.MARCH.UPPER.CASE');
				break;

			case 3:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.APRIL.UPPER.CASE');
				break;

			case 4:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.MAY.UPPER.CASE');
				break;

			case 5:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JUNE.UPPER.CASE');
				break;

			case 6:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JULY.UPPER.CASE');
				break;

			case 7:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.AUGUST.UPPER.CASE');
				break;

			case 8:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.SEPTEMBER.UPPER.CASE');
				break;

			case 9:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.OCTOBER.UPPER.CASE');
				break;

			case 10:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.NOVEMBER.UPPER.CASE');
				break;

			case 11:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.DECEMBER.UPPER.CASE');
				break;
		}

		return nameMonth;
	}

	getYear(): number {
		return this.dateAttachment.getFullYear();
	}

	buttonNext() {

		this.submitted = true;
		this.paymentSignatureForm.markAllAsTouched();
		this.paymentSignatureForm.updateValueAndValidity();

		if (this.paymentSignatureForm.invalid) {
			if (this.f['termsUser']?.errors) {
				const focusedTerms = this.focusTermsConsent();
				if (!focusedTerms && this.f['signature']?.errors) {
					setTimeout(() => this.signaturePadCmp?.focusCanvas());
				}
				return;
			}

			if (this.f['signature']?.errors) {
				setTimeout(() => this.signaturePadCmp?.focusCanvas());
			}
			return;
		}

		this.setSignatureImgBase64();
		if (!this.signatureImgBase64) {
			this.paymentSignatureForm.get('signature')?.setErrors({ required: true });
			setTimeout(() => this.signaturePadCmp?.focusCanvas());
			return;
		}
		this.save();
	}

	downloadPDF2(): void {
		const element = document.getElementById('pdfPaymentMethod');
		if (!element) {
			return;
		}

		import('jspdf').then(({ jsPDF }) => {
			const doc = new jsPDF({ unit: 'in', format: 'a4', orientation: 'portrait' });
			doc.html(element, {
				callback: (pdf) => {
					pdf.save('TERMO_CONTRATACAO_GARANTIA_ESTENDIDA_REDUZIDA.pdf');
				},
				margin: [0, 0, 0, 0],
				x: 0,
				y: 0,
				width: 8.27,
				windowWidth: element.scrollWidth
			});
		});
	}

	save(): void {

		this.isSubmitted = true;
		this.paymentMethodService.setContractPaymentSignature(this.setContractPaymentSignature())
		this.paymentMethodService.setPaymentContractSummaryShort(this.paymentContractSummaryShort);
		this.vinValidate = new VinValidate();
		this.choosePlanBuyng = new ChoosePlanBuyng();
		this.clientData = new ClientData();
		this.contractPaymentSignature = new ContractPaymentSignature();
		this.paymentContractSummaryShort = new PaymentContractSummaryShort;
		this.blockedUi = false;

		this.router.navigate(['payment-method'], {
			queryParams: {
				uuid: this.uuid
			}
		});
	}

	contractStartDate(): Date {
		let date = new Date();
		if (this.vinValidate.warrantyStartDate !== null) {
			date = this.vinValidate.warrantyStartDate;
		}
		return date;
	}

	setSignatureImgBase64(): void {
		if (this.signaturePadCmp) {
			this.signatureImgBase64 = this.imageService.getSignatureDataUrl(this.signaturePadCmp);
		}
	}

	fordImageLogo(valor: number) {
		const element = document.getElementById("fordImage");
		if (element) {
			if (valor === 1) {
				element.style.display = "";
			} else {
				element.style.display = "none";
			}
		}
	}

	exportPDF() {
		this.showLogo();
		this.downloadPDF();
		this.hideLogo();
	}

	showLogo() {
		this.fordImageLogo(1);
	}

	hideLogo() {
		setTimeout(this.fordImageLogo, 10000);
	}

	private setContractPaymentSignature(): ContractPaymentSignature {

		const contractPaymentSignature = new ContractPaymentSignature();

		contractPaymentSignature.contractId = this.paymentContractSummaryShort.contractId;
		contractPaymentSignature.user = ConstantsFordProtect.EMISSION_USER;

		contractPaymentSignature.files.type = FileType.PURCHASE_TERM;
		contractPaymentSignature.files.name = FileType.PURCHASE_TERM_NAME;
		contractPaymentSignature.files.description = FileType.PURCHASE_TERM_DESCRIPTION;
		contractPaymentSignature.files.fileReference = FileType.PURCHASE_TERM.toString();
		contractPaymentSignature.files.file = this.signatureImgBase64;

		return contractPaymentSignature;
	}

	downloadPDF() {
		if (this.isDownloadingContract) {
			return;
		}

		this.isDownloadingContract = true;
		this.contractService.downloadFile(this.contractIdDownload)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: DownloadFile) => {

				const source = response.pdfByteArray;
				const link = document.createElement("a");

				link.href = source;
				link.download = FileType.CONTRACT_TERMS_NAME;
				link.click();

				this.isDownloadingContract = false;
			}, (error) => {

				this.isDownloadingContract = false;
				switch (error.status) {

					case 401:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					case 400:
					case 404:
					case 500:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.FILE.NOT.FOUND')
						});
						break;

					default:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});

	}

}
